import { axios } from '@/plugins/http-axios';
import cons from '@/store/cons';
import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { IData } from '@/types/globalTypes';
import { IFile, TClientDoc } from '@/types/docTypes';
import { isLkkAppClient } from '@/types';

export interface IEnvLkk {
  [key: string]: string | boolean | string[];
}

export interface IFlag {
  code: string;
  desc: string;
  value: boolean;
  affectedSystems: string[];
}

export interface IParameter {
  lkbSystemsVisible: boolean;
  code: string;
  desc: string;
  value: string;
  resulttype: string;
}

/** Ответ info/systeminfo/: флаги, параметры и словарь банка */
export interface ISystemInfo {
  flags: IFlag[];
  parameters: IParameter[];
  i18n: Record<string, string>;
}

export const useEnvStore = defineStore('env', () => {
  // State
  const envFlags = ref<IFlag[]>([]);
  const envParameters = ref<IParameter[]>([]);
  const envLkk = ref<IEnvLkk>({});
  const consData = ref<IData>(cons);

  // Getter для получения флага по коду
  const flag = computed(() => (code: string): boolean => {
    const flagItem = envFlags.value.find((flag) => flag.code === code);
    return flagItem ? flagItem.value : false;
  });

  // Getter для получения параметра по коду
  const sysParameter = computed(
    () =>
      (code: string): IParameter | undefined =>
        envParameters.value.find((p) => p.code === code),
  );

  // Computed getters
  const getCons = computed(() => consData.value);

  const isIntegrationRadar = computed(() => flag.value('INTEGRATION_RADAR'));
  const isRequestForAdditionalDoc = computed(() => flag.value('REQUEST_FOR_ADDITIONAL_DOCUMENTS'));
  const isDarkTheme = computed(() => flag.value('DARK_THEME_ACTIVATE'));
  const finalResultContract = computed(() => sysParameter.value('FINAL_RESULT')?.value === 'contract' || false);
  const isAgent = computed(() => flag.value('ENABLE_LKK_AGENT'));
  const isNewQuestionnaireMode = computed(() => flag.value('IS_NEW_QUESTIONNAIRE_MODE'));
  const isCreditConveyor = computed(() => flag.value('CREDIT_CONVEYOR_ENABLE'));
  const isHideOffice = computed(() => flag.value('HIDE_OFFICE'));
  const isComment = computed(() => flag.value('SHOW_COMMENT'));
  const isDynamicSec = computed(() => flag.value('DYNAMIC_SECTIONS'));
  const isTwoSideSigningOfDocumentsEp = computed(() => flag.value('BILATERAL_SIGNING_OF_DOCUMENTS_EP'));
  const isSigningUnepDocument = computed(() => flag.value('SIGNING_UNEP_DOCUMENTS'));
  const isUnepFioSn = computed(() => flag.value('UNEP_FIO_SN'));
  const isRequestDigitalProfileFl = computed(() => flag.value('REQUEST_DIGITAL_PROFILE_FL'));
  const isDiadoc = computed(() => flag.value('DIADOC_ENABLE'));
  const hideSectionsEnable = computed(() => flag.value('HIDE_SECTIONS_ENABLE'));
  const isDynamicSectionAppData = computed(() => flag.value('DYNAMIC_SECTION_APPDATA_ENABLE'));
  const isConveyorIndividuals = computed(() => flag.value('CONVEYOR_INDIVIDUALS'));
  const isSmartanApi = computed(() => flag.value('SMARTAN_API'));
  const isCredConvXPIntegShowOption = computed(() => flag.value('CREDCONV_XP_INTEG_SHOW_OPTION'));
  const isOriginFilenameEnable = computed(() => flag.value('ORIGIN_FILENAME_ENABLE'));
  const isRequestForAdditionalDocuments = computed(() => flag.value('REQUEST_FOR_ADDITIONAL_DOCUMENTS'));
  const isMonitoringJournalEnable = computed(() => flag.value('MONITORING_JOURNAL_ENABLE'));
  const isEmailInsteadOfParticipant = computed(() => flag.value('EMAIL_INSTEAD_OF_PARTICIPANT'));
  const isConfirmParentValue = computed(() => flag.value('QUESTIONNAIRE_CONFIRM_PARENT_VALUE'));
  const isShowOrgStatusEnabled = computed(() => envLkk.value.SHOW_ORG_STATUS_ENABLED);
  const showOrgStatus = computed(() => (isShowOrgStatusEnabled.value ? envLkk.value.SHOW_ORG_STATUS : []));
  const isSignClientsDocuments = computed(() => flag.value('SIGN_CLIENTS_DOCUMENTS'));
  const isSimpleReg = computed(() => flag.value('REGISTRATION_WITHOUT_EMAIL'));
  const isHidePromocode = computed(() => flag.value('HIDE_PROMOCODE'));
  //Изменение номера телефона
  const isChangingPhoneNum = computed(() => flag.value('CHANGING_PHONE_NUM'));
  //Вложение документа при переходе
  const isAttachNextStatus = computed(() => flag.value('ATTACH_NEXT_STATUS'));
  const isOrigProvided = computed(() => flag.value('ORIG_PROVIDED'));
  const isCorrespondentBankEnabled = computed(() => flag.value('ENABLE_CORRESPONDENT_BANK'));
  //Интеграция КК с АБС Диасофт (ROS-1344)
  const isDiasoftIntegrationEnabled = computed(() => flag.value('DIASOFT_INTEGRATION_ENABLED'));
  const isMultipleValidation = computed(() => flag.value('MULTIPLE_VALIDATION'));
  const isDefinitionAgent = computed(() => flag.value('DEFINITION_AGENT'));
  const isDownloadingTheClientQuestionnaire = computed(() => flag.value('DOWNLOADING_THE_CLIENT_QUESTIONNAIRE'));
  const isUlNoRes = computed(() => flag.value('UL_NONRES')); //УЛ по нерезидентам
  const isAbsClientStatus = computed(() => flag.value('ABS_CLIENT_STATUS'));
  const isCollaboraEnable = computed(() => flag.value('COLLABORA_ENABLE'));

  // Сложный getter с зависимостью от другого store
  const trBgColorClientDoc = (file: IFile, docType: TClientDoc): string => {
    const colorStore = useColorStore();

    if (!isLkkAppClient(docType.subType)) {
      if (file.doc?.confirmResult) {
        if (file.doc.confirm) {
          return colorStore.theme.customerDocumentsValid;
        } else {
          return colorStore.theme.customerDocumentsNotValid;
        }
      }
      if (file.isSignFinished) {
        return colorStore.theme.customerDocumentsValid;
      }
    }

    if (file.type === consData.value.APP_DOC_TYPE_REQ) {
      return '#ddd';
    }

    switch (file.status) {
      case consData.value.APP_DOC_STATUS_FOR_CHECK:
        return colorStore.theme.customerDocumentsForCheck;
      case consData.value.APP_DOC_STATUS_VALID:
        return colorStore.theme.customerDocumentsValid;
      case consData.value.APP_DOC_STATUS_NOT_VALID:
        return colorStore.theme.customerDocumentsNotValid;
      case consData.value.APP_DOC_STATUS_PROCESS:
        return colorStore.theme.customerDocumentsProcess;
      default:
        return '';
    }
  };

  // Actions
  const getEnvLkk = async () => {
    const mainStore = useMainStore();

    try {
      const resp = await axios.get<IEnvLkk>(mainStore.getUri.env);
      envLkk.value = resp.data;
      return Promise.resolve();
    } catch (e: any) {
      return Promise.reject(e);
    }
  };

  const getEnv = async () => {
    const mainStore = useMainStore();
    const i18nStore = useI18nStore();

    try {
      const resp = await axios.get<ISystemInfo>(mainStore.getUri.infoSystemInfo);
      i18nStore.setI18nBank(resp.data.i18n);
      envFlags.value = resp.data.flags;
      envParameters.value = resp.data.parameters;

      const title = document.querySelector('title');
      if (title) {
        title.innerHTML = resp.data.i18n.LKB_TITLE || '';
      }

      return Promise.resolve();
    } catch (e: any) {
      return Promise.reject(e);
    }
  };

  const setEnv = (data: Pick<ISystemInfo, 'flags' | 'parameters'>) => {
    envFlags.value = data.flags;
    envParameters.value = data.parameters;
  };

  const setEnvLkk = (data: IEnvLkk = {}) => {
    envLkk.value = data;
  };

  // Возвращаем все state, computed и actions
  return {
    // State (как ref)
    env: {
      flags: envFlags,
      parameters: envParameters,
      lkk: envLkk,
    },
    cons: consData,

    // Computed
    envLkk: computed(() => envLkk.value),
    getCons,

    // Getters как функции
    flag,
    sysParameter,
    trBgColorClientDoc,

    // Computed флаги
    isRequestForAdditionalDoc,
    isDarkTheme,
    finalResultContract,
    isAgent,
    isNewQuestionnaireMode,
    isCreditConveyor,
    isHideOffice,
    isComment,
    isDynamicSec,
    isTwoSideSigningOfDocumentsEp,
    isSigningUnepDocument,
    isUnepFioSn,
    isRequestDigitalProfileFl,
    isDiadoc,
    hideSectionsEnable,
    isDynamicSectionAppData,
    isConveyorIndividuals,
    isSmartanApi,
    isCredConvXPIntegShowOption,
    isOriginFilenameEnable,
    isRequestForAdditionalDocuments,
    isMonitoringJournalEnable,
    isEmailInsteadOfParticipant,
    isConfirmParentValue,
    isShowOrgStatusEnabled,
    showOrgStatus,
    isSignClientsDocuments,
    isSimpleReg,
    isHidePromocode,
    isIntegrationRadar,
    isChangingPhoneNum,
    isAttachNextStatus,
    isOrigProvided,
    isCorrespondentBankEnabled,
    isDiasoftIntegrationEnabled,
    isMultipleValidation,
    isDefinitionAgent,
    isDownloadingTheClientQuestionnaire,
    isUlNoRes,
    isAbsClientStatus,
    isCollaboraEnable,

    // Actions
    getEnvLkk,
    getEnv,
    setEnv,
    setEnvLkk,
  };
});

// Импорты других store (должны быть после определения useEnvStore или в начале файла)
import { useMainStore } from './mainStore';
import { useI18nStore } from './i18n';
import { useColorStore } from './color';

export default useEnvStore;
